
import { useState, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Gift, Tag, Percent, ShoppingBag } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import TranslatedText from '@/TranslatedText';

interface SlideProps {
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  colorClass: string;
  icon: React.ReactNode;
}

const slides: SlideProps[] = [{
  title: "Summer Sale",
  description: "Enjoy up to 50% off on all summer items. Limited time offer!",
  ctaText: "Shop Now",
  ctaLink: "/summer-sale",
  colorClass: "bg-blue-600 text-white",
  icon: <Percent className="w-6 h-6 md:w-8 md:h-8" />
}, {
  title: "New Arrivals",
  description: "Check out our latest products. Fresh styles just for you!",
  ctaText: "Discover",
  ctaLink: "/new-arrivals",
  colorClass: "bg-red-600 text-white",
  icon: <Tag className="w-6 h-6 md:w-8 md:h-8" />
}, {
  title: "Special Coupon",
  description: "Use code WELCOME20 to get 20% off on your first purchase!",
  ctaText: "Get Code",
  ctaLink: "/coupons",
  colorClass: "bg-amber-500 text-black",
  icon: <Gift className="w-6 h-6 md:w-8 md:h-8" />
}, {
  title: "Flash Deals",
  description: "24-hour deals on popular products. Grab them before they're gone!",
  ctaText: "View Deals",
  ctaLink: "/flash-deals",
  colorClass: "bg-ahavex-blue text-white",
  icon: <ShoppingBag className="w-6 h-6 md:w-8 md:h-8" />
}];

const HeroCarousel = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="w-full relative max-w-full overflow-hidden">
      <Carousel 
        opts={{
          align: "start",
          loop: true
        }} 
        setApi={api => {
          if (api) {
            api.on("select", () => {
              setActiveSlide(api.selectedScrollSnap());
            });
            api.scrollTo(activeSlide);
          }
        }} 
        className="w-full max-w-full"
      >
        <CarouselContent>
          {slides.map((slide, index) => (
            <CarouselItem key={index}>
              <div className={cn("w-full h-[220px] md:h-[250px] p-4 md:p-8 flex flex-col justify-center", slide.colorClass)}>
                <div className="max-w-5xl mx-auto w-full flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-8">
                  <div className="flex justify-center items-center bg-white bg-opacity-20 p-3 rounded-full">
                    {slide.icon}
                  </div>
                  
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
                      <TranslatedText text={slide.title} />
                    </h2>
                    <p className="mb-3 md:mb-4 text-xs md:text-sm opacity-90">
                      <TranslatedText text={slide.description} />
                    </p>
                    
                    <Button size={isMobile ? "sm" : "default"} variant={slide.colorClass.includes("amber") ? "outline" : "secondary"} className={slide.colorClass.includes("amber") ? "border-black text-black hover:bg-amber-600" : ""} asChild>
                      <a href={slide.ctaLink}>
                        <TranslatedText text={slide.ctaText} />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        <div className="flex justify-center mt-2 mb-4">
          {slides.map((_, index) => (
            <button 
              key={index} 
              onClick={() => setActiveSlide(index)} 
              className={`w-2 h-2 md:w-3 md:h-3 mx-1 rounded-full transition-all ${activeSlide === index ? "bg-ahavex-blue" : "bg-gray-300"}`} 
              aria-label={`Go to slide ${index + 1}`} 
            />
          ))}
        </div>
      </Carousel>
    </div>
  );
};

export default HeroCarousel;
